// Partner Portal — Concierge restyle (2026-06-25).
//
// Wraps the existing partner sub-components (DownlinePyramid,
// PipelineProjectionCard, LayoutColumns, LayoutAccordion) in the new
// shared <PortalShell> chrome so this portal matches the client portal:
// real horizontal Solara lockup in the nav, spiral icon watermark, cream
// page bg, font-serif headlines, navy/gold KPI tiles.
//
// Behaviour preserved 1:1:
//   - Two layout modes (columns / accordion) toggle persisted in
//     localStorage at "solara.partner.layout".
//   - Single dashboard fetch feeds both layouts; client + deal lists
//     fetched on demand and cached.
//   - Auth gate bounces non-partners to their home route.
//
// New for the Concierge style:
//   - 4-tile KPI strip up top (downline / active deals / pipeline / YTD
//     commission) sourced from dashboard.totals.
//   - <SolaraContactCard> embedded near the top so partners always have a
//     one-click email path to loans@sgcap.com.au regardless of layout.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, LayoutGrid, Rows3, Settings } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useNoIndex } from "@/lib/useNoIndex";
import { formatCurrency as fmtCurrency } from "@/lib/formatters";
import LayoutColumns from "@/components/partner/LayoutColumns";
import LayoutAccordion from "@/components/partner/LayoutAccordion";
import DownlinePyramid from "@/components/partner/DownlinePyramid";
import PipelineProjectionCard from "@/components/partner/PipelineProjectionCard";
import PortalShell, {
  PortalGreeting,
  KpiStrip,
  KpiTile,
  SolaraContactCard,
} from "@/components/portal/PortalShell";
// PR Portal-V2-Partner-Introducer-Messaging (2026-06-26) — adds a hybrid
// broker-contact + per-deal messaging panel to the Partner portal.
import PortalMessagingHub from "@/components/portal/PortalMessagingHub";
// TASK-2 (2026-07-10) — tasks assigned to this partner by Solara.
import PortalTasksCard from "@/components/portal/PortalTasksCard";
import { isStaffRole } from "@/lib/roles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LAYOUT_KEY = "solara.partner.layout";
const DEFAULT_LAYOUT = "columns";

function readStoredLayout() {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (raw === "columns" || raw === "accordion") return raw;
  } catch (_) { /* localStorage may be blocked */ }
  return DEFAULT_LAYOUT;
}

function fmtMoney0(n) {
  return fmtCurrency(Number(n) || 0, 0);
}

export default function PartnerPortal() {
  useNoIndex();
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();

  const [layout, setLayout] = useState(readStoredLayout);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientsCache, setClientsCache] = useState({});
  const [dealsCache, setDealsCache] = useState({});
  // Portal-V2-Partner-Messaging (2026-06-26) — flat deal list for the
  // messaging hub. Lazy-fetched alongside the dashboard; survives across
  // layout toggles.
  const [allDeals, setAllDeals] = useState([]);

  // Auth gate.
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (user.role !== "partner") {
      // HF-Team-Vis-1 (2026-07-29) — junior_broker is staff too.
      if (isStaffRole(user)) navigate("/admin", { replace: true });
      else if (user.role === "introducer") navigate("/introducer", { replace: true });
      else navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Single dashboard fetch.
  useEffect(() => {
    if (!user || user.role !== "partner") return;
    let cancelled = false;
    (async () => {
      try {
        // Portal-V2-Partner-Messaging (2026-06-26) — fetch dashboard +
        // flat all-deals in parallel. The all-deals endpoint is the lean
        // projection used by the messaging hub picker.
        const [dashRes, dealsRes] = await Promise.all([
          axios.get(`${API}/partner/dashboard`),
          axios.get(`${API}/partner/all-deals`).catch(() => ({ data: { deals: [] } })),
        ]);
        if (!cancelled) {
          setDashboard(dashRes.data);
          setAllDeals(dealsRes.data?.deals || []);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Failed to load partner dashboard");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const toggleLayout = (next) => {
    if (next === layout) return;
    setLayout(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch (_) { /* ignore */ }
  };

  // On-demand fetchers (cached) — unchanged from Portals-3.
  const fetchIntroducerClients = async (introducerId) => {
    if (clientsCache[introducerId]) return clientsCache[introducerId];
    try {
      const res = await axios.get(`${API}/partner/introducers/${introducerId}/clients`);
      setClientsCache((prev) => ({ ...prev, [introducerId]: res.data }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load clients");
      throw err;
    }
  };
  const fetchDirectClients = async () => {
    if (clientsCache.__direct__) return clientsCache.__direct__;
    try {
      const res = await axios.get(`${API}/partner/direct/clients`);
      setClientsCache((prev) => ({ ...prev, __direct__: res.data }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load your clients");
      throw err;
    }
  };
  const fetchClientDeals = async (clientId) => {
    if (dealsCache[clientId]) return dealsCache[clientId];
    try {
      const res = await axios.get(`${API}/partner/clients/${clientId}/deals`);
      setDealsCache((prev) => ({ ...prev, [clientId]: res.data }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load deals");
      throw err;
    }
  };
  const fetchDirectDeals = async () => {
    if (dealsCache.__direct_deals__) return dealsCache.__direct_deals__;
    try {
      const res = await axios.get(`${API}/partner/direct/deals`);
      setDealsCache((prev) => ({ ...prev, __direct_deals__: res.data }));
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load your deals");
      throw err;
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0ede4]">
        <div className="animate-spin w-8 h-8 border-4 border-[#bd9a59] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0ede4] text-gray-500">
        Partner dashboard unavailable. Please contact Solara.
      </div>
    );
  }

  const fetchers = {
    fetchIntroducerClients,
    fetchDirectClients,
    fetchClientDeals,
    fetchDirectDeals,
  };

  const partner = dashboard.partner || {};
  const totals = dashboard.totals || {};
  const downlineCount = (totals.introducer_count ?? 0) + (totals.jr_broker_count ?? 0);
  const firstName = (partner.name || "").split(" ")[0] || partner.name || "";

  const subtitle = downlineCount > 0
    ? `${downlineCount} in your downline · ${totals.active_deal_count_all ?? 0} active deal${totals.active_deal_count_all === 1 ? "" : "s"} · ${fmtMoney0(totals.pipeline_total_all)} pipeline`
    : `${totals.active_deal_count_all ?? 0} active deal${totals.active_deal_count_all === 1 ? "" : "s"} · ${fmtMoney0(totals.pipeline_total_all)} pipeline`;

  const rightActions = (
    <>
      <div className="hidden md:inline-flex bg-white/10 rounded-md p-0.5">
        <button
          onClick={() => toggleLayout("columns")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            layout === "columns" ? "bg-[#bd9a59] text-[#021d49]" : "text-white/80 hover:text-white"
          }`}
          data-testid="partner-layout-columns"
        >
          <LayoutGrid className="w-3.5 h-3.5" /> Columns
        </button>
        <button
          onClick={() => toggleLayout("accordion")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
            layout === "accordion" ? "bg-[#bd9a59] text-[#021d49]" : "text-white/80 hover:text-white"
          }`}
          data-testid="partner-layout-accordion"
        >
          <Rows3 className="w-3.5 h-3.5" /> Stacked
        </button>
      </div>
      <Link
        to="/settings"
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
        data-testid="partner-settings"
      >
        <Settings className="w-4 h-4" />
        <span className="hidden md:inline">Settings</span>
      </Link>
      <button
        onClick={handleLogout}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
        data-testid="partner-logout"
      >
        <LogOut className="w-4 h-4" />
        <span className="hidden md:inline">Sign out</span>
      </button>
    </>
  );

  return (
    <PortalShell portalLabel="Partner Portal" brandHref="/partner" rightActions={rightActions}>
      <PortalGreeting
        title={firstName ? `Good day, ${firstName}` : "Welcome back"}
        subtitle={subtitle}
      />

      <KpiStrip>
        <KpiTile
          label="Downline"
          value={downlineCount}
          sub={
            totals.jr_broker_count
              ? `${totals.introducer_count ?? 0} introducer${totals.introducer_count === 1 ? "" : "s"} · ${totals.jr_broker_count} jr broker${totals.jr_broker_count === 1 ? "" : "s"}`
              : null
          }
        />
        <KpiTile
          label="Active deals"
          value={totals.active_deal_count_all ?? 0}
        />
        <KpiTile
          label="Pipeline"
          value={fmtMoney0(totals.pipeline_total_all)}
        />
        <KpiTile
          label="YTD commission"
          value={fmtMoney0(totals.ytd_partner_commission_earned)}
          accent
          sub={
            totals.pipeline_partner_commission_projection
              ? `+ ${fmtMoney0(totals.pipeline_partner_commission_projection)} projected`
              : null
          }
        />
      </KpiStrip>

      {/* TASK-2 (2026-07-10) — tasks assigned to this partner by Solara.
          Renders nothing when no tasks exist, keeping the dashboard clean. */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4">
        <PortalTasksCard />
      </div>

      {/* Always-visible payout projection + pyramid (existing components) */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4 space-y-4">
        <PipelineProjectionCard totals={dashboard.totals} />
        <DownlinePyramid
          partner={dashboard.partner}
          downline={dashboard.introducers}
        />
      </div>

      {/* Solara contact card + Messaging hub — visible on every layout.
          Portal-V2-Partner-Messaging (2026-06-26): contact card on the
          left, per-deal messaging hub on the right two columns. */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SolaraContactCard
            title="Your Solara contact"
            name={partner.assigned_admin_name || "Solara Loans Team"}
            role={partner.assigned_admin_name ? "Your Solara contact" : "Partner support"}
            email={partner.assigned_admin_email || "loans@sgcap.com.au"}
          />
          <div className="md:col-span-2">
            <PortalMessagingHub
              deals={allDeals}
              title="Message Solara about a deal"
              emptyHint="Once your downline has active deals you can message Solara about any of them here."
              storageKey="partner"
            />
          </div>
        </div>
      </div>

      {/* Drill-down layout (existing components — content untouched) */}
      {layout === "columns" ? (
        <LayoutColumns
          dashboard={dashboard}
          clientsCache={clientsCache}
          dealsCache={dealsCache}
          fetchers={fetchers}
        />
      ) : (
        <LayoutAccordion
          dashboard={dashboard}
          clientsCache={clientsCache}
          dealsCache={dealsCache}
          fetchers={fetchers}
          onJumpToColumns={(introducerId) => {
            toggleLayout("columns");
            try { sessionStorage.setItem("solara.partner.columns.selectedIntroducer", introducerId); }
            catch (_) { /* ignore */ }
          }}
        />
      )}
    </PortalShell>
  );
}
